import { useEffect, useMemo, useState } from "react";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RepeatIcon from "@mui/icons-material/Repeat";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import { useAddFocusSessionMutation, useGetFocusHistoryQuery } from "../../api/personalApi";
import { useGetGroupProgressQuery, useGetMyGroupsQuery } from "../../api/groupsApi";
import { SoftButton, SoftFooter, cls, useDialogPaperSx } from "../hatim/soft/SoftKit";
import { ACTIVITY_LABEL_KEYS } from "../goals/goalRules";
import { minuteGoals } from "../../lib/dayGoals";
import { localDateString, startOfMonth } from "../../lib/dateUtils";
import {
  POMODORO,
  PRESETS,
  formatClock,
  initialSession,
  nextPomodoroSession,
  sessionEndDate,
  workedMinutes,
} from "../../lib/focusTimer";

const MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

/** "2026-09-21" → "21 Eylül" */
function formatDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] || ""}` : value || "";
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${
        active
          ? "bg-terakkiPrimaryDark text-white dark:bg-white dark:text-terakkiPrimaryDark"
          : `${cls.chip} hover:bg-terakkiBrandMist dark:hover:bg-white/[0.16]`
      }`}
    >
      {children}
    </button>
  );
}

function SummaryStrip({ stats }) {
  return (
    <div className={`shrink-0 flex ${cls.summary}`}>
      {stats.map((s, i) => (
        <div
          key={s.label}
          className={`flex-1 px-4 py-3 text-center ${i > 0 ? "border-l border-terakkiBorder dark:border-white/[0.14]" : ""}`}
        >
          <div className={`text-lg font-medium ${cls.title}`}>{s.value}</div>
          <div className={`text-[11px] ${cls.muted}`}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Odaklan (mobil FocusModal): serbest kip ileri sayar, süreli kip geri,
 * pomodoro turlu çalışır. Seans bitince geçen dakika kaydedilir; dakika
 * birimli bir hedef seçildiyse o hedefin o günkü değerine de eklenir.
 * Sayaç Terakkim'de yaşar (timer); pencere kapansa da seans sürer.
 */
export default function FocusDialog({ open, onClose, timer, onSaved, localDate }) {
  const { t } = useScriptMode();
  const { user } = useAuth();
  const toast = useToast();
  const paperSx = useDialogPaperSx();
  const { session, elapsed, remaining, finished, start, stop } = timer;

  const [mode, setMode] = useState("zamanlayici");
  const [minutes, setMinutes] = useState(25);
  const [goalKey, setGoalKey] = useState(null);
  const [tab, setTab] = useState("odak");
  const [groupId, setGroupId] = useState(null);

  // Seçili günde geçerli dakika hedefleri; gün değişip seçili hedef düşerse serbest seansa dönülür.
  const goals = useMemo(() => minuteGoals(user?.goalDetail, localDate), [user, localDate]);
  useEffect(() => {
    if (goalKey && !goals.some((g) => g.key === goalKey)) setGoalKey(null);
  }, [goals, goalKey]);
  const [addFocusSession, { isLoading: saving }] = useAddFocusSessionMutation();
  const { data: historyResp, isLoading: historyLoading } = useGetFocusHistoryQuery({ days: 30 }, { skip: !open });
  const history = historyResp?.data || null;

  const { data: groupsResp } = useGetMyGroupsQuery(undefined, { skip: !open || tab !== "istatistik" });
  const groups = useMemo(() => groupsResp?.data?.groups || [], [groupsResp]);
  const activeGroupId = groupId || groups[0]?.id || null;
  const today = localDateString(new Date());
  const { data: progressResp, isFetching: progressLoading } = useGetGroupProgressQuery(
    { groupId: activeGroupId, startDate: localDateString(startOfMonth(new Date())), endDate: today },
    { skip: !open || tab !== "istatistik" || !activeGroupId }
  );
  const ranking = useMemo(
    () =>
      (progressResp?.data?.members || [])
        .filter((m) => (m.focusMinutes || 0) > 0)
        .sort((a, b) => (b.focusMinutes || 0) - (a.focusMinutes || 0)),
    [progressResp]
  );

  const onStart = () => start(initialSession({ mode, minutes, goalKey }));
  const onNextRound = () => session && start(nextPomodoroSession(session));

  /** Seansı bitirir; en az bir dakika çalışıldıysa kaydeder. */
  const onFinish = async () => {
    if (!session) return;
    const now = Date.now();
    const spent = workedMinutes(session, now);
    const goal = goals.find((g) => g.key === session.goalKey);
    const rounds = session.mode === "pomodoro" ? session.round : 0;
    const localDate = localDateString(sessionEndDate(session, now));
    const mode = session.mode;
    stop();
    if (spent < 1) {
      toast.error(t("focus_too_short"));
      return;
    }
    const result = await addFocusSession({
      minutes: spent,
      localDate,
      goalLabel: goal?.label || null,
      activity: goal?.activity || null,
      mode,
      rounds,
    });
    if (result.error) {
      toast.error(result.error.message || t("focus_save_failed"));
      return;
    }
    toast.success(t("focus_saved_minutes", { minutes: spent }));
    if (onSaved) onSaved();
  };

  const clock = session
    ? formatClock(session.targetSeconds > 0 ? remaining : elapsed)
    : formatClock(mode === "zamanlayici" ? minutes * 60 : mode === "pomodoro" ? POMODORO.work * 60 : 0);

  const status = session
    ? session.mode === "pomodoro"
      ? `${session.isBreak ? t("focus_break") : t("focus_work")} · ${session.round}. ${t("focus_round")}`
      : finished
        ? t("focus_time_up")
        : t("focus_running")
    : t("focus_how_long");

  const todayStat = history?.days?.find((d) => d.localDate === today);
  const listTab = !session && tab !== "odak";

  const renderTimer = () => (
    <div className="flex flex-col items-center gap-3 px-5 py-6">
      <div
        className={`text-6xl font-light tabular-nums tracking-tight ${finished ? cls.accent : cls.title}`}
        data-testid="focus-clock"
        aria-live="polite"
      >
        {clock}
      </div>
      <div className={`text-sm ${cls.muted}`}>{status}</div>

      {session ? null : (
        <>
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Chip active={mode === "odak"} onClick={() => setMode("odak")}>
              {t("focus_free")}
            </Chip>
            {PRESETS.map((value) => (
              <Chip
                key={value}
                active={mode === "zamanlayici" && value === minutes}
                onClick={() => {
                  setMode("zamanlayici");
                  setMinutes(value);
                }}
              >
                {`${value} ${t("focus_min")}`}
              </Chip>
            ))}
            <Chip active={mode === "pomodoro"} onClick={() => setMode("pomodoro")}>
              {t("focus_pomodoro")}
            </Chip>
          </div>
          {mode === "pomodoro" ? (
            <p className={`text-xs text-center ${cls.muted}`}>
              {t("focus_pomodoro_hint", {
                work: POMODORO.work,
                short: POMODORO.shortBreak,
                rounds: POMODORO.roundsBeforeLong,
                long: POMODORO.longBreak,
              })}
            </p>
          ) : mode === "odak" ? (
            <p className={`text-xs text-center ${cls.muted}`}>{t("focus_free_hint")}</p>
          ) : null}

          {goals.length > 0 ? (
            <div className="self-stretch pt-2">
              <div className={`text-xs font-medium pb-1 ${cls.muted}`}>{t("focus_which_goal")}</div>
              <ul role="radiogroup" aria-label={t("focus_which_goal")}>
                {[{ key: null, label: t("focus_free_session"), activity: null, goalValue: null }, ...goals].map((g) => {
                  const active = g.key === goalKey;
                  return (
                    <li key={g.key || "serbest"}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setGoalKey(g.key)}
                        className={`w-full flex items-center gap-3 px-1 py-2.5 text-left ${cls.row} ${cls.rowHover}`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                            active ? "border-terakkiPrimaryDark dark:border-[#8FC9CA]" : "border-terakkiBorder dark:border-white/45"
                          }`}
                        >
                          {active ? <span className="w-2 h-2 rounded-full bg-terakkiPrimaryDark dark:bg-[#8FC9CA]" /> : null}
                        </span>
                        <span className={`flex-1 min-w-0 truncate text-sm ${cls.title}`}>{g.label}</span>
                        {g.key ? (
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${cls.chip}`}>
                            {t(ACTIVITY_LABEL_KEYS[g.activity] || ACTIVITY_LABEL_KEYS.okuma)}
                          </span>
                        ) : null}
                        {g.key ? <span className={`text-xs ${cls.muted}`}>{`${g.goalValue} ${t("focus_min")}`}</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  const renderHistory = () => {
    if (historyLoading && !history) {
      return (
        <div className="p-5 flex flex-col gap-3">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`h-5 rounded ${cls.skeleton}`} />
          ))}
        </div>
      );
    }
    if (!history || !history.sessions?.length) {
      return <p className={`px-5 py-10 text-center text-sm ${cls.muted}`}>{t("focus_no_sessions")}</p>;
    }
    return (history.days || []).map((day) => (
      <div key={day.localDate}>
        <div className={`flex items-center justify-between px-5 pt-4 pb-1.5 text-xs ${cls.muted}`}>
          <span className="font-medium">{day.localDate === today ? t("focus_today") : formatDay(day.localDate)}</span>
          <span>{`${day.minutes} ${t("focus_min")} · ${day.sessions} ${t("focus_sessions")}`}</span>
        </div>
        <ul>
          {history.sessions
            .filter((row) => row.localDate === day.localDate)
            .map((row, i) => (
              <li key={`${row.finishedAt}-${i}`} className={`flex items-center gap-3 px-5 py-2.5 ${cls.row}`}>
                {row.mode === "pomodoro" ? (
                  <RepeatIcon sx={{ fontSize: 16 }} className={cls.accent} aria-hidden />
                ) : (
                  <TimerOutlinedIcon sx={{ fontSize: 16 }} className={cls.accent} aria-hidden />
                )}
                <span className={`flex-1 min-w-0 truncate text-sm ${cls.title}`}>{row.goalLabel || t("focus_free")}</span>
                <span className={`text-sm ${cls.muted}`}>{`${row.minutes} ${t("focus_min")}`}</span>
              </li>
            ))}
        </ul>
      </div>
    ));
  };

  const renderStats = () => {
    if (!groups.length) {
      return <p className={`px-5 py-10 text-center text-sm ${cls.muted}`}>{t("focus_no_groups")}</p>;
    }
    return (
      <div>
        <div className={`px-5 pt-4 pb-2 text-xs font-medium ${cls.muted}`}>{t("focus_group_ranking")}</div>
        {groups.length > 1 ? (
          <div className="px-5 pb-2 flex flex-wrap gap-2">
            {groups.map((g) => (
              <Chip key={g.id} active={g.id === activeGroupId} onClick={() => setGroupId(g.id)}>
                {g.groupName}
              </Chip>
            ))}
          </div>
        ) : null}
        {progressLoading && !ranking.length ? (
          <div className="p-5 flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`h-5 rounded ${cls.skeleton}`} />
            ))}
          </div>
        ) : ranking.length === 0 ? (
          <p className={`px-5 py-8 text-center text-sm ${cls.muted}`}>{t("focus_no_ranking")}</p>
        ) : (
          <ul>
            {ranking.map((m, i) => (
              <li
                key={m.id}
                className={`flex items-center gap-3 px-5 py-2.5 ${cls.row} ${m.isMe ? "bg-terakkiBrandCloud dark:bg-white/[0.08]" : ""}`}
              >
                <span className={`w-5 text-sm ${cls.muted}`}>{i + 1}</span>
                <span className={`flex-1 min-w-0 truncate text-sm ${cls.title}`}>{`${m.name || ""} ${m.surname || ""}`}</span>
                <span className={`text-sm ${cls.muted}`}>{`${m.focusMinutes} ${t("focus_min")}`}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const n = (v) => (historyLoading && !history ? "·" : v ?? 0);

  return (
    <Dialog
      open={!!open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { ...paperSx, height: "min(720px, calc(100vh - 64px))" } }}
    >
      <div className="flex flex-col h-full min-h-0">
        <div className="shrink-0 flex items-center justify-between px-5 pt-4 pb-2">
          <span className={`text-lg font-medium ${cls.title}`}>{t("focus_title")}</span>
          <IconButton size="small" onClick={onClose} disabled={saving} aria-label={t("close")}>
            <CloseIcon fontSize="small" className={cls.muted} />
          </IconButton>
        </div>

        {/* Sayaç çalışırken sekme değiştirilmez; pencere sayaca ayrılır. */}
        {!session ? (
          <div className="shrink-0 flex gap-1 px-5 pb-3" role="tablist">
            {[
              { key: "odak", label: t("focus_tab_focus") },
              { key: "gecmis", label: t("focus_tab_history") },
              { key: "istatistik", label: t("focus_tab_stats") },
            ].map((item) => (
              <Chip key={item.key} active={tab === item.key} onClick={() => setTab(item.key)}>
                {item.label}
              </Chip>
            ))}
          </div>
        ) : null}

        {listTab && tab === "gecmis" ? (
          <SummaryStrip
            stats={[
              { value: n(history?.totalMinutes), label: `${t("focus_last_30")} · ${t("focus_min")}` },
              { value: n(history?.sessions?.length), label: t("focus_sessions") },
            ]}
          />
        ) : null}
        {listTab && tab === "istatistik" ? (
          <SummaryStrip
            stats={[
              { value: n(todayStat?.minutes), label: `${t("focus_today_lower")} · ${t("focus_min")}` },
              { value: n(history?.totalMinutes), label: t("focus_last_30") },
              { value: n(history?.sessions?.length), label: t("focus_sessions") },
            ]}
          />
        ) : null}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {tab === "odak" || session ? renderTimer() : tab === "gecmis" ? renderHistory() : renderStats()}
        </div>

        {session ? (
          <SoftFooter className="flex-col">
            {session.mode === "pomodoro" ? (
              <SoftButton tone="primary" fullWidth onClick={onNextRound} disabled={saving}>
                {session.isBreak ? t("focus_next_round") : t("focus_to_break")}
              </SoftButton>
            ) : null}
            <div className="flex gap-2">
              <SoftButton tone="cancel" fullWidth onClick={stop} disabled={saving}>
                {t("focus_cancel")}
              </SoftButton>
              <SoftButton tone="success" fullWidth onClick={onFinish} disabled={saving}>
                {t("focus_finish")}
              </SoftButton>
            </div>
          </SoftFooter>
        ) : tab === "odak" ? (
          <SoftFooter>
            <SoftButton tone="primary" fullWidth startIcon={<PlayArrowIcon />} onClick={onStart}>
              {t("focus_start")}
            </SoftButton>
          </SoftFooter>
        ) : null}
      </div>
    </Dialog>
  );
}
